import { AptiveIcon } from '@aptive-env/storybook'
import { ICalendarEventType } from 'interface/calendar'
import { FC, useEffect, useRef, useState, useMemo } from 'react'
import ImageCheckboxChecked from 'assets/image/checkbox-checked.png'
import ImageCheckboxUnchecked from 'assets/image/checkbox-unchecked.png'

interface MultiEventSelectProps {
  options: ICalendarEventType[]
  value: ICalendarEventType[]
  onChange: (value: ICalendarEventType[]) => void
}
const MultiEventSelect: FC<MultiEventSelectProps> = ({
  options,
  value,
  onChange
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  
  const handleSelect = (item: ICalendarEventType) => {
    if(!value.includes(item)) onChange([...value, item])
    if(value.includes(item)) onChange(value.filter(type => type !== item))
  }

  const isAllOptionsSelected = useMemo(() => {
    return options.length === value.length
  }, [options, value])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof HTMLElement && ref.current && !ref.current.contains(event.target)) {
        event.stopPropagation()
        setIsExpanded(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  const handleClickSelectAll = () => {
    if (!isAllOptionsSelected) {
      onChange(options)
    } else {
      onChange([])
    }
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md h-[38px] px-2 py-2">
      <div className="relative">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <div className="mx-1 text-[14px] text-[#4B5563]">Events</div>
          <AptiveIcon className="w-[20px] h-[20px] stroke-aro-gray fill-none" icon={isExpanded ? 'chevronUp' : 'chevronDown'} />
        </div>
        {isExpanded && (
          <div
            ref={ref}
            className="shadow rounded-lg overflow-hidden absolute z-[100] bg-white w-[200px] -ml-2 mt-3"
          >
            <div
              className="item flex items-center h-9 px-3 cursor-pointer border-b hover:bg-ui-gray-100 "
              onClick={handleClickSelectAll}
            >
              <div>
                {isAllOptionsSelected ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="16" height="16" rx="4" fill="white"/>
                    <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill="#007AFF" stroke="#007AFF"/>
                    <path d="M3 7.5C3 7.22386 3.22386 7 3.5 7H12.5C12.7761 7 13 7.22386 13 7.5V8.5C13 8.77614 12.7761 9 12.5 9H3.5C3.22386 9 3 8.77614 3 8.5V7.5Z" fill="white"/>
                  </svg>
                ) : (
                  <img src={ImageCheckboxUnchecked} alt="ImageCheckboxUnchecked" />
                )}
              </div>
              <div className="text-ui-gray-600 text-[14px] ml-2">Select all</div>
            </div>
            {options.map((item, index) => (
              <div
                key={index}
                className="h-9 flex items-center whitespace-nowrap px-3 cursor-pointer border-b hover:bg-ui-gray-100"
                onClick={() => handleSelect(item)}
              >
                <div>
                  {value.includes(item) ? (
                    <img src={ImageCheckboxChecked} alt="ImageCheckboxChecked" />
                  ) : (
                    <img src={ImageCheckboxUnchecked} alt="ImageCheckboxUnchecked" />
                  )}
                </div>
                <div className="ml-2 text-[14px] text-[#4B5563]">{item.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MultiEventSelect
